import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { GraphQlService } from '@shared/services/graphql-service';
import { catchError, map, switchMap, of } from 'rxjs';
import { ConsigneesActions } from './consignees.actions';
import { LIST_MY_CONSIGNEES, SAVE_CONSIGNEES, DELETE_CONSIGNEE, GET_CONSIGNEE_BY_UUID } from '@modules/external-services/shared/graphql/external-shared.graphql';

@Injectable()
export class ConsigneesEffects {
  private readonly actions$ = inject(Actions);
  private readonly graphQLService = inject(GraphQlService);

  private buildFailure(errorMessage?: string) {
    return {
      success: false,
      statusCode: 400,
      message: errorMessage ?? 'Network error',
      data: null,
    };
  }

  loadConsignees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsigneesActions.loadAll),
      switchMap(({ pageableParam }) =>
        this.graphQLService
          .query<any>({
            query: LIST_MY_CONSIGNEES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam },
          })
          .pipe(
            map((result) => {
              const data = result.data?.listMyConsignees;
              if (!data) {
                return ConsigneesActions.loadAllFailure({
                  error: this.buildFailure('No data returned'),
                });
              }
              return ConsigneesActions.loadAllSuccess({
                content: data.content ?? [],
                totalElements: data.totalElements ?? 0,
              });
            }),
            catchError((error) =>
              of(ConsigneesActions.loadAllFailure({ error: this.buildFailure(error?.message) })),
            ),
          ),
      ),
    ),
  );

  saveConsignee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsigneesActions.save),
      switchMap(({ input }) =>
        this.graphQLService
          .mutate<any>({
            mutation: SAVE_CONSIGNEES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input },
          })
          .pipe(
            map((result) => {
              const data = result.data?.saveConsignee;
              if (data?.status === 'SUCCESS' || data?.status === 'CREATED' || data?.status === true) {
                return ConsigneesActions.saveSuccess({ entity: data.data });
              }
              return ConsigneesActions.saveFailure({
                error: this.buildFailure(data?.message ?? 'Failed to save consignee'),
              });
            }),
            catchError((error) =>
              of(ConsigneesActions.saveFailure({ error: this.buildFailure(error?.message) })),
            ),
          ),
      ),
    ),
  );

  loadConsigneeDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsigneesActions.loadDetails),
      switchMap(({ uuid }) =>
        this.graphQLService
          .query<any>({
            query: GET_CONSIGNEE_BY_UUID,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid },
          })
          .pipe(
            map((result) => {
              const res = result.data?.getConsigneeByUuid;
              if (res?.status === 'SUCCESS' || res?.status === 'CREATED' || res?.status === true) {
                return ConsigneesActions.loadDetailsSuccess({ entity: res.data });
              }
              return ConsigneesActions.loadDetailsFailure({
                error: this.buildFailure(res?.message ?? 'Failed to load details'),
              });
            }),
            catchError((error) =>
              of(ConsigneesActions.loadDetailsFailure({ error: this.buildFailure(error?.message) })),
            ),
          ),
      ),
    ),
  );

  deleteConsignee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsigneesActions.delete),
      switchMap(({ uuid }) =>
        this.graphQLService
          .mutate<any>({
            mutation: DELETE_CONSIGNEE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid },
          })
          .pipe(
            map((result) => {
              const res = result.data?.deleteConsignee;
              if (res?.status === 'SUCCESS' || res?.status === 'CREATED' || res?.status === true) {
                return ConsigneesActions.deleteSuccess({ uuid });
              }
              return ConsigneesActions.deleteFailure({
                error: this.buildFailure(res?.message ?? 'Failed to delete consignee'),
              });
            }),
            catchError((error) =>
              of(ConsigneesActions.deleteFailure({ error: this.buildFailure(error?.message) })),
            ),
          ),
      ),
    ),
  );
}
